import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject, of } from 'rxjs';
import { catchError, filter, map, shareReplay, take, takeUntil } from 'rxjs/operators';
import { integrationActions } from '../../actions/integration.actions';
import { IAumicoCompanySummary } from '../../models/integration/company-mapping-suggestion.model';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { IntegrationProvider } from '../../provider/integration.provider';
import { IntegrationService } from '../../services/integration.service';

/**
 * Mapping modal for Abacus: links one Aumico Company to one Abacus Mandanten ID.
 *
 * Two modes:
 *  - **Add** (`aumicoCompanyId` input is null): picker visible, user searches
 *    for an Aumico Company, then types the Mandanten ID, then Save.
 *  - **Edit** (`aumicoCompanyId` set): Aumico Company shown as read only, only
 *    the Mandanten ID input is editable. Save replaces the existing mapping.
 *
 * Unlike D365 BC's single edit modal, this does NOT go through the `singleEdit`
 * NgRx slice — the parent integrations-overview owns the open/close state via
 * @Input bindings. The modal dispatches `upsertMapping` on Save and closes on
 * the `upsertMappingSuccess` action.
 */
@Component({
    selector: 'aumico-abacus-mapping-modal',
    templateUrl: './abacus-mapping-modal.component.html',
    styleUrls: ['./abacus-mapping-modal.component.scss'],
})
export class AbacusMappingModalComponent implements OnInit, OnDestroy {
    /** The Abacus IntegrationConnection this mapping belongs to. Required. */
    @Input() public connectionId!: string;
    /**
     * Pre selected Aumico Company id when editing an existing mapping. Null when
     * adding a new one. The picker becomes read only in edit mode.
     */
    @Input() public aumicoCompanyId: string | null = null;

    @Output() public closed = new EventEmitter<void>();

    /** Form state. */
    public selectedAumicoCompany: IAumicoCompanySummary | null = null;
    public mandantenId = '';

    /**
     * Catalog fetch state, passed down to the shared search input component
     * (`aumico-integrations-search-company-input-field`), which owns the search
     * term, filtering and dropdown rendering.
     */
    public searchIsLoading = true;
    public searchError = false;

    /** Cached catalog stream, passed into the search input component as `options`. */
    public aumicoCatalog$!: Observable<IAumicoCompanySummary[]>;
    private readonly destroy$ = new Subject<void>();

    public get isEditMode(): boolean {
        return this.aumicoCompanyId != null;
    }

    /** Form valid when both an Aumico Company is selected AND a Mandanten ID is entered. */
    public get isFormValid(): boolean {
        return this.selectedAumicoCompany != null && /^\d+$/.test(this.mandantenId.trim());
    }

    public constructor(
        private readonly integrationProvider: IntegrationProvider,
        private readonly integrationService: IntegrationService,
        private readonly actions$: Actions,
    ) {}

    public ngOnInit(): void {
        // Load the Aumico Company catalog once. shareReplay caches it so a
        // search retype doesn't refetch.
        this.aumicoCatalog$ = this.integrationService.listAumicoCompanyOptions().pipe(
            map((companies) => {
                this.searchIsLoading = false;
                return companies;
            }),
            catchError(() => {
                this.searchIsLoading = false;
                this.searchError = true;
                return of([] as IAumicoCompanySummary[]);
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );

        // In Edit mode: pre fill selectedAumicoCompany + mandantenId from the
        // existing mapped companies cache for this connection. We read from
        // `tenantDetailCompanies` which the manage modal already loaded.
        if (this.isEditMode) {
            this.integrationProvider
                .getTenantDetailCompanies$()
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe((rows) => {
                    const row = rows.find((r) => r.id === this.aumicoCompanyId);
                    if (row != null) {
                        this.selectedAumicoCompany = { id: row.id, name: row.name };
                        this.mandantenId = row.mapping.externalCompanyId;
                    }
                });
        }

        // Auto close on successful persist.
        this.actions$
            .pipe(
                ofType(integrationActions.upsertMappingSuccess),
                filter(({ companyId }) =>
                    this.selectedAumicoCompany != null &&
                    companyId === this.selectedAumicoCompany.id,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.closed.emit();
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Form actions ────────────────────────────────────────────────────────
    //
    // The Aumico Company selection is owned end to end by the shared
    // `aumico-integrations-search-company-input-field` via two-way binding
    // (`[(selectedCompany)]="selectedAumicoCompany"`). The shared component
    // also renders the staged card and the "Change" affordance internally,
    // so we don't need an onSelect / onClear handler here.

    public onSave(): void {
        if (!this.isFormValid || this.selectedAumicoCompany == null) return;
        const mandantenId = this.mandantenId.trim();
        this.integrationProvider.upsertMapping(this.selectedAumicoCompany.id, {
            vendor: IntegrationVendor.ABACUS,
            connectionId: this.connectionId,
            externalCompanyId: mandantenId,
            // Abacus exposes no Mandant name API, so we mirror the ID into the
            // cached display name. The BE keeps both fields filled per schema,
            // and the UI just shows the ID either way.
            externalCompanyName: mandantenId,
        });
    }

    public onCancel(): void {
        this.closed.emit();
    }
}
